import { useEffect } from "react";

import { EmptyObject } from "@hen/stdlib/types";
import {
  IntercomProps,
  IntercomProvider,
  IntercomProviderProps,
  useIntercom,
} from "react-use-intercom";

import { useTrackerAuth } from "components/AuthProvider";
import { UserWithCompany } from "lib/frontend/cm/utils/types";
import { User } from "prisma/cm/client";

function getPrimaryEmail(user: UserWithCompany): string | undefined {
  return user?.emails?.find((email) => email.isPrimary)?.email;
}

function getDisplayEmail(user: UserWithCompany): string | null {
  if (!user?.emails) return null;
  return user.emails.length > 1 ? getPrimaryEmail(user) || null : user.emails[0]?.email || null;
}

/**
 * We don't provide Intercom in all environments so we make appId
 * optional.
 */
type BenchmarkingIntercomProviderProps = Partial<Pick<IntercomProviderProps, "appId">> & {
  children: React.ReactNode;
};

export function getIntercomInstanceData(
  user: UserWithCompany | null,
  impersonatorUser: User | null = null,
): EmptyObject | IntercomProps {
  if (!user) return {};

  // When impersonating, use the impersonator's intercom session hash (the "outer" user)
  const intercomSessionHash = impersonatorUser?.intercomSessionHash || user.intercomSessionHash;

  return {
    userId: user.id,
    userHash: intercomSessionHash || undefined,
    name: user.name,
    email: getDisplayEmail(user) || undefined,
    company: {
      companyId: String(user.companyId),
      name: user.company?.name,
      customAttributes: {
        salesforce_id: user.company?.salesforceId,
      },
    },
  };
}

/**
 * Component that updates Intercom when user changes.
 * Must be rendered inside IntercomProvider.
 */
function IntercomUserUpdater() {
  const { user, impersonatorUser } = useTrackerAuth();
  const intercom = useIntercom();

  useEffect(() => {
    if (user) {
      try {
        intercom.update(getIntercomInstanceData(user, impersonatorUser));
      } catch (error) {
        console.error("Failed to update Intercom:", error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, impersonatorUser]);

  return null;
}

/**
 * Wraps IntercomProvider to conditionally not inject intercom if the
 * appId is not provided. Uses TrackerAuthProvider for user data instead of Redux.
 */
export function BenchmarkingIntercomProvider({
  children,
  appId,
}: BenchmarkingIntercomProviderProps) {
  const { user, impersonatorUser } = useTrackerAuth();

  if (!appId) {
    return <>{children}</>;
  }

  return (
    <IntercomProvider
      appId={appId}
      autoBoot
      autoBootProps={getIntercomInstanceData(user, impersonatorUser)}
      shouldInitialize
    >
      <IntercomUserUpdater />
      {children}
    </IntercomProvider>
  );
}
